import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Button, CircularProgress } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ToolTipComponent from '../../../pages/PrimeFigma/Components/ToolTipComponent';
import NewVoucherSettingsModal from '../../../pages/PrimeFigma/Modals/newVoucherSettingModal';
import { useListGeneralSettingQuery } from '../../../redux/apis/VoucherSettings.api';
import { useAppSelector } from '../../../redux';

interface Props {
  label: string;
  manualmode?: boolean;
  name: string;
  type: string;
  value: string;
  onChange: ({ mannual, value }: { mannual: boolean; value: string }) => void;
  error?: boolean;
  helperText?: string;
}

const VoucherSettingsAuto: React.FC<Props> = ({ label, manualmode, name, type, value, onChange, helperText = '' }) => {
  console.log('VoucherSettingsAuto props:', { label, manualmode, name, type, value, helperText });
  const [settingsModal, setSettingsModal] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0); // 👈 used to trigger data reload when modal mode changes
  const userPref = useAppSelector((state) => state?.userPrefrenceandReducer?.financialYear);

  // --- Fetch voucher settings ---
  const {
    data: generalSettings,
    isLoading,
    refetch,
  } = useListGeneralSettingQuery(
    {
      entrytypecd: type,
      financialyearid: userPref?.financialyearid,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: manualmode === true,
    }
  );

  useEffect(() => {
    if (!manualmode) {
      refetch();
    }
  }, [manualmode, refetch, refetchKey]);

  const [voucherOptions, setVoucherOptions] = useState<string[]>([]);
  const [manualVoucherFlag, setManualVoucherFlag] = useState(false);

  useEffect(() => {
    if (manualmode || !generalSettings?.listGeneralSetting) {
      setVoucherOptions([]);
      setManualVoucherFlag(false);
      return;
    }

    let manualFlag = false;
    const list = generalSettings.listGeneralSetting || [];
    const mapped = list
      .map((elem) => {
        if (elem?.expiredtimestamp === 0 && elem?.nextinvoicenumber === -1) {
          manualFlag = true;
          return null;
        } else {
          return (elem?.prefix || '') + (elem?.nextinvoicenumber || '') + (elem?.suffix || '');
        }
      })
      .filter(Boolean) as string[];

    setManualVoucherFlag(manualFlag);
    setVoucherOptions(mapped);
    console.log('VoucherSettingsAuto - voucherOptions:', mapped, 'value:', value, 'found:', mapped.find(opt => opt === value));
  }, [generalSettings, manualmode]);

  // Debug logging for Autocomplete
  useEffect(() => {
    console.log('Autocomplete render - voucherOptions:', voucherOptions, 'value:', value, 'isLoading:', isLoading);
  }, [voucherOptions, value, isLoading]);

  const effectiveManual = manualmode !== undefined ? manualmode : manualVoucherFlag;

  const handleChange = (newValue: string | null, isManual: boolean = false) => {
    onChange?.({ mannual: isManual, value: newValue || '' });
  };

  // --- Handle modal close callback ---
  const handleModalChange = (resData: any) => {
    // After mode changed in modal, reload UI
    setRefetchKey((prev) => prev + 1);
    refetch();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {effectiveManual ? (
        <TextField
          fullWidth
          size="small"
          label={label}
          value={value}
          onChange={(e) => handleChange(e.target.value, true)}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiInputBase-input': { fontSize: '14px', padding: '8.5px 7px' },
            '& .MuiFormLabel-root': { fontSize: '14px' },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'input.border',
            },
          }}
        />
      ) : (
        <Autocomplete
          key={`voucher-autocomplete-${value}-${voucherOptions.length}`}
          sx={{ width: '100%' }}
          openOnFocus
          disablePortal
          disableClearable
          size="small"
          loading={isLoading}
          options={voucherOptions}
          value={(() => {
            const autocompleteValue = value ? value : undefined;
            console.log('Autocomplete value being set:', { value, autocompleteValue, voucherOptions });
            return autocompleteValue;
          })()}
          isOptionEqualToValue={(option, value) => {
            const isEqual = option === value;
            console.log('isOptionEqualToValue check:', { option, value, isEqual });
            return isEqual;
          }}
          onChange={(_, newValue) => handleChange(newValue, false)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={label}
              helperText={helperText}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {/* Settings icon */}
      <ToolTipComponent
        title="Voucher Number Settings"
        placement="top"
        tooltipContent={
          <Button sx={{ minWidth: 'auto', padding: 0, ml: 1 }} onClick={() => setSettingsModal(true)}>
            <SettingsIcon sx={{ color: '#b5b5b5', fontSize: '1.5rem' }} />
          </Button>
        }
      />

      {settingsModal && (
        <NewVoucherSettingsModal
          open={settingsModal}
          label={name}
          type={type}
          setOpen={setSettingsModal}
          onChange={() => handleModalChange(undefined)}
        />
      )}
    </Box>
  );
};

export { VoucherSettingsAuto };
